import { Button } from "@msuite/ui/button";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { ModalWrapper } from "@msuite/ui/modal";
import type { IncomingInvoiceDetail, Nullable } from "@msuite/utils/types";
import { getPayableAmounts, isIncomingInvoiceLocked } from "@msuite/utils/incoming-invoices";
import React from "react";
import { useCitadel } from "@/hooks/use-citadel";
import { EditCostAllocationModal } from "@/modals/incoming-invoices/edit-cost-allocation-modal";

/** Props Interface */
interface CostAllocationMenuProps {
	incomingInvoice: Nullable<IncomingInvoiceDetail>;
}

/**
 * The edit of the cost allocation. Out of reach without a net amount to split, once the invoice
 * is paid, and for a caller without `update IncomingInvoice` — the route refuses all three.
 */
export function CostAllocationMenu({ incomingInvoice }: CostAllocationMenuProps) {
	/** Context */
	const { t } = useTranslation();

	/** Hooks */
	const { citadel } = useCitadel();
	const editModal = useDisclosure();

	/** Computed */
	// The payable net amount: the reduced one once an approver applied a Kürzung.
	const netAmount = incomingInvoice ? getPayableAmounts(incomingInvoice).netAmount : null;
	const isDisabled =
		!incomingInvoice ||
		netAmount === null ||
		isIncomingInvoiceLocked(incomingInvoice.state) ||
		(!!citadel && citadel.cannot("update", "IncomingInvoice"));

	/** Render */
	return (
		<React.Fragment>
			<Button
				icon={<Icon name="Edit" />}
				variant="primary"
				onClick={editModal.onOpen}
				isDisabled={isDisabled}
				data-cy="edit-cost-allocation-button"
			>
				{t("incoming_invoices.edit_cost_allocation")}
			</Button>
			<ModalWrapper>
				{editModal.isOpen && incomingInvoice && netAmount !== null && (
					<EditCostAllocationModal
						incomingInvoice={incomingInvoice}
						netAmount={netAmount}
						isOpen={editModal.isOpen}
						onClose={editModal.onClose}
					/>
				)}
			</ModalWrapper>
		</React.Fragment>
	);
}
